import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { Redirect, Route, Switch, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Intercom from 'containers/ConnectedIntercom';
import Header from 'containers/Header';

//Route components..
import DashRedirect from 'containers/DashRedirect';
import NewSignupRedirect from 'containers/NewSignupRedirect';
import UpgradeRedirect from 'containers/UpgradeRedirect';
import TeamDash from 'containers/pages/TeamDash';
import UserDash from 'containers/pages/UserDash';
import EventDash from 'containers/pages/EventDash';

//Modal components
import BroadcastModal from 'containers/modals/BroadcastModal';
import EditEventModal from 'containers/modals/EditEventModal';
import CreateEventModal from 'containers/modals/CreateEventModal';
import DuplicateEventModal from 'containers/modals/DuplicateEventModal';
import DeleteEventModal from 'containers/modals/DeleteEventModal';
import RenameFolderModal from 'containers/modals/RenameFolderModal';
import RenameEventDocModal from 'containers/modals/RenameEventDocModal';
import CreateFolderModal from 'containers/modals/CreateFolderModal';
import EditTeamModal from 'containers/modals/EditTeamModal';
import InviteEventCrewModal from 'containers/modals/InviteEventCrewModal';
import InviteTeamAdminsModal from 'containers/modals/InviteTeamAdminsModal';
import DeleteFSObjectModal from 'containers/modals/DeleteFSObjectModal';
import UserContext from 'containers/contexts/UserContext.js';
import CreateTeamModal from 'containers/modals/CreateTeamModal';
import InviteCrewModal from 'containers/modals/InviteCrewModal';
import VerifyEmailModal from 'containers/modals/VerifyEmailModal';

import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';

import DragLayer from 'containers/DragLayer';

import Paths from './AppPaths';
import { AppErrorBoundary } from '@shoflo/polymer/components/ErrorBoundary/AppErrorBoundary';
import useLaunchDarkly from '@shoflo/polymer/hooks/useLaunchDarkly/useLaunchDarkly';
import { getEnvVar } from '@studio/environment/getEnvVar';
import { initRUM } from '@studio/logger/datadog/initRum';
import packageJSON from '../package.json';

const { version, name: appName } = packageJSON;

const Footer = styled.footer`
  text-align: center;
  padding-top: 40px;
`;

const FooterContent = styled.div`
  margin-bottom: 8px;
`;

//Global styles
const Main = styled.main`
  padding: 0 33px;

  @media only screen and (max-width: 768px) {
    padding: 0 15px;
  }
`;

// eslint-disable-next-line react/prop-types
const App = ({ user }) => {

  initRUM(appName, version);

  const defaultUser = {
    _id: 'dashboard-anonymous',
    firstName: 'unauthenticated-dashboard',
    email: 'dashboard-anonymous@shoflo.io',
    createdOn: Date.now()
  };

  const ldKey = getEnvVar('REACT_APP_LAUNCHDARKLY_KEY');

  const isLDInitializedOrTimedOut = useLaunchDarkly(
    { user: user ?? defaultUser },
    ldKey
  );

  if (!isLDInitializedOrTimedOut) return <></>;

  return (
    <AppErrorBoundary>
      <UserContext.Provider value={user}>
        <Intercom />
        <Header />
        <Main>
          {/* Custom drag layer */}
          <DragLayer />

          {/* Routes */}
          <Route exact path={Paths.DashRedirect} component={DashRedirect} />
          <Route
            exact
            path={Paths.NewSignupRedirect}
            component={NewSignupRedirect}
          />
          <Route
            exact
            path={Paths.UpgradeRedirect}
            component={UpgradeRedirect}
          />

          <Switch>
            <Redirect
              exact
              from={Paths.EventDash}
              to={`${Paths.EventDash}/dashboard`}
            />
            <Route path={Paths.EventDash} component={EventDash} />
            <Route path={Paths.TeamDash} component={TeamDash} />
          </Switch>
          <Route exact path={Paths.UserDash} component={UserDash} />

          {/* Modals that can be activated from multiple pages */}
          <CreateEventModal />
          <DuplicateEventModal />
          <EditEventModal />
          <DeleteEventModal />
          <CreateFolderModal />
          <EditTeamModal />
          <InviteEventCrewModal />
          <InviteTeamAdminsModal />
          <DeleteFSObjectModal />
          <RenameFolderModal />
          <RenameEventDocModal />
          <BroadcastModal />
          <CreateTeamModal />
          <InviteCrewModal />
          <VerifyEmailModal />
        </Main>
        <Footer>
          <FooterContent />
        </Footer>
      </UserContext.Provider>
    </AppErrorBoundary>
  );
};

App.propTypes = {
  user: PropTypes.object
};

const DndApp = DragDropContext(HTML5Backend)(App);

export default withRouter(connect(({ user }) => ({ user }), {})(DndApp));
